import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { auth } from './firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { createUserProfileIfNotExists, getUserProfile, subscribeToUserProfile } from './userProfile';
import { captureAttribution } from './attribution';
import { requestWelcomeEmailIfNeeded } from './emailApi';
import { UserProfile } from './types';
import { currentMarket } from './market';
import { ThemeProvider } from './ThemeContext';
import Navbar from './Navbar';
import Plans from './Plans';
import Developer from './Developer';
import CallCenter from './CallCenter';
import CallDetail from './CallDetail';
import AdminDashboard from './AdminDashboard';
import AdminUserDetail from './AdminUserDetail';
import AdminCallDetail from './AdminCallDetail';
import { isAdminEmail } from './adminApi';
import './App.css';

type AuthMode = 'signIn' | 'signUp' | 'forgotPassword';

function AppContent() {
  const navigate = useNavigate();
  const market = currentMarket();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [authMode, setAuthMode] = useState<AuthMode>('signIn');

  useEffect(() => {
    // Seed first-touch attribution as early as possible so direct-to-app
    // traffic (e.g. app.callout.jp/signup?utm_...) is captured before any
    // in-app navigation strips the URL params.
    captureAttribution();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoadError('');
      try {
        if (currentUser) {
          const isNewUser = await createUserProfileIfNotExists(currentUser);
          const profile = await getUserProfile(currentUser.uid);

          if (isNewUser && currentUser.providerData[0]?.providerId === 'google.com') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'sign_up', method: 'google' });
          }
          setUserProfile(profile);

          // Welcome email right after sign-up: Google accounts are verified at
          // once, email/password accounts on their first verified sign-in.
          requestWelcomeEmailIfNeeded(currentUser, profile);

          // for local firebase token testing
          const token = await currentUser?.getIdToken();
          console.log('Firebase Token:', token);
        } else {
          setUserProfile(null);
        }
      } catch (err) {
        // Without this the page sits on "Loading..." forever, e.g. when a DNS
        // filter, VPN or ad blocker blocks firestore.googleapis.com.
        console.error('Failed to load user profile:', err);
        setLoadError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Keep the profile current while signed in, so credits a purchase adds
  // (the payment webhook writes them a moment later) show without a reload.
  useEffect(() => {
    if (!user) return;
    return subscribeToUserProfile(user.uid, (profile) => {
      if (profile) setUserProfile(profile);
    });
  }, [user]);

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const switchAuthMode = (mode: AuthMode) => {
    setAuthMode(mode);
    clearMessages();
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Send verification email
      await sendEmailVerification(userCredential.user);

      // Sign the user out so they must verify before signing in
      await signOut(auth);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'sign_up', method: 'email' });

      setSuccessMessage('Account created! Please check your email to verify your account before signing in.');
      setEmail('');
      setPassword('');
      setAuthMode('signIn');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // If email is not verified, block sign-in
      if (!result.user.emailVerified) {
        await signOut(auth);
        setError('Please verify your email before signing in. Check your inbox for the verification link.');
        return;
      }

      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleGoogleSignIn = async () => {
    clearMessages();
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage('Password reset email sent! Check your inbox for a link to reset your password.');
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleResendVerification = async () => {
    clearMessages();
    if (!email) {
      setError('Please enter the email address you signed up with.');
      return;
    }
    try {
      // Sign in temporarily to resend verification
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user.emailVerified) {
        setSuccessMessage('Your email is already verified. You can sign in.');
        await signOut(auth);
        return;
      }
      await sendEmailVerification(result.user);
      await signOut(auth);
      setSuccessMessage('Verification email resent! Please check your inbox.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const triggerPhoneCall = async () => {
    try {
      const token = await user!.getIdToken();
      const response = await fetch('YOUR_CLOUD_RUN_URL/api/call', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: '+1234567890',
          message: 'Test call from Call For Me'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to trigger call');
      }

      const data = await response.json();
      alert('Call triggered successfully: ' + JSON.stringify(data));
    } catch (err) {
      setError('Error triggering call: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="App"><header className="App-header">Loading...</header></div>;
  }

  if (loadError) {
    return (
      <div className="App">
        <header className="App-header">
          <p>Couldn't load your account.</p>
          <p style={{ fontSize: '16px', maxWidth: '420px' }}>
            Please check your internet connection. A VPN, ad blocker or network
            filter may be blocking firestore.googleapis.com.
          </p>
          <p style={{ fontSize: '13px', opacity: 0.7 }}>{loadError}</p>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer' }}>
            Retry
          </button>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar user={user} onSignOut={handleSignOut} />

      <Routes>
        <Route path="/plans" element={<Plans />} />
        <Route
          path="/developer"
          element={user ? <Developer /> : <Navigate to="/" replace />}
        />
        <Route
          path="/calls/:callId"
          element={user ? <CallDetail /> : <Navigate to="/" replace />}
        />
        <Route
          path="/admin"
          element={
            user && isAdminEmail(user.email) ? (
              <AdminDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/calls/:callId"
          element={
            user && isAdminEmail(user.email) ? (
              <AdminCallDetail />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/admin/users/:uid"
          element={
            user && isAdminEmail(user.email) ? (
              <AdminUserDetail />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={
          user ? (
            <CallCenter
              userProfile={userProfile}
              onProfileUpdate={setUserProfile}
            />
          ) : (
          <header className="App-header">
            <h1>Callout</h1>
            <p>{market.tagline[0]}<br/>{market.tagline[1]}<br/>{market.tagline[2]}</p>

            {error && <div style={{ color: '#ff6b6b', margin: '10px', padding: '10px', borderRadius: '5px', backgroundColor: 'var(--error-bg)' }}>{error}</div>}
            {successMessage && <div style={{ color: '#51cf66', margin: '10px', padding: '10px', borderRadius: '5px', backgroundColor: 'var(--success-bg)' }}>{successMessage}</div>}

            <div style={{ maxWidth: '300px', margin: '20px auto' }}>
                <h2>
                  {authMode === 'signIn' && 'Sign In'}
                  {authMode === 'signUp' && 'Create Account'}
                  {authMode === 'forgotPassword' && 'Reset Password'}
                </h2>

                {/* Google Sign-In Button (not shown on forgot password) */}
                {authMode !== 'forgotPassword' && (
                  <>
                    <button
                      onClick={handleGoogleSignIn}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        cursor: 'pointer',
                        backgroundColor: '#4285f4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                    >
                      <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" fillRule="evenodd">
                          <path d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.6v2.2h3a8.8 8.8 0 0 0 2.6-6.6z" fill="#4285F4" />
                          <path d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.2a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z" fill="#34A853" />
                          <path d="M4 10.7a5.4 5.4 0 0 1 0-3.4V5H1a9 9 0 0 0 0 8l3-2.3z" fill="#FBBC05" />
                          <path d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.4a5.4 5.4 0 0 1 5-3.7z" fill="#EA4335" />
                        </g>
                      </svg>
                      Sign in with Google
                    </button>

                    <div style={{ margin: '20px 0', color: 'var(--text-muted)' }}>or</div>
                  </>
                )}

                {/* Forgot Password Form */}
                {authMode === 'forgotPassword' && (
                  <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 10px 0' }}>
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ padding: '10px', fontSize: '14px', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)' }}
                    />
                    <button
                      type="submit"
                      style={{ padding: '10px', fontSize: '14px', cursor: 'pointer', backgroundColor: '#4285f4', color: 'white', border: 'none', borderRadius: '5px' }}
                    >
                      Send Reset Link
                    </button>
                    <button
                      type="button"
                      onClick={() => switchAuthMode('signIn')}
                      style={{ background: 'none', border: 'none', color: '#4285f4', cursor: 'pointer', fontSize: '14px', marginTop: '5px' }}
                    >
                      Back to Sign In
                    </button>
                  </form>
                )}

                {/* Sign In Form */}
                {authMode === 'signIn' && (
                  <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ padding: '10px', fontSize: '14px', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)' }}
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ padding: '10px', fontSize: '14px', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)' }}
                    />
                    <button
                      type="submit"
                      style={{ padding: '10px', fontSize: '14px', cursor: 'pointer', backgroundColor: '#4285f4', color: 'white', border: 'none', borderRadius: '5px' }}
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => switchAuthMode('forgotPassword')}
                      style={{ background: 'none', border: 'none', color: '#4285f4', cursor: 'pointer', fontSize: '13px', marginTop: '2px' }}
                    >
                      Forgot Password?
                    </button>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}
                    >
                      Resend Verification Email
                    </button>
                    <div style={{ marginTop: '10px', color: 'var(--text-muted)', fontSize: '14px' }}>
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => switchAuthMode('signUp')}
                        style={{ background: 'none', border: 'none', color: '#4285f4', cursor: 'pointer', fontSize: '14px', padding: 0 }}
                      >
                        Sign Up
                      </button>
                    </div>
                  </form>
                )}

                {/* Sign Up Form */}
                {authMode === 'signUp' && (
                  <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ padding: '10px', fontSize: '14px', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)' }}
                    />
                    <input
                      type="password"
                      placeholder="Password (min 6 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{ padding: '10px', fontSize: '14px', backgroundColor: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)' }}
                    />
                    <button
                      type="submit"
                      style={{ padding: '10px', fontSize: '14px', cursor: 'pointer', backgroundColor: '#51cf66', color: 'white', border: 'none', borderRadius: '5px' }}
                    >
                      Create Account
                    </button>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px', lineHeight: '1.5' }}>
                      By signing up, you agree to our{' '}
                      <a href={market.termsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4285f4' }}>Terms of Service</a>
                      {' '}and{' '}
                      <a href={market.privacyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4285f4' }}>Privacy Policy</a>.
                    </p>
                    <div style={{ marginTop: '10px', color: 'var(--text-muted)', fontSize: '14px' }}>
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => switchAuthMode('signIn')}
                        style={{ background: 'none', border: 'none', color: '#4285f4', cursor: 'pointer', fontSize: '14px', padding: 0 }}
                      >
                        Sign In
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </header>
          )
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
